import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Eye,
  Search,
  ShoppingBag,
  Target,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getMilestoneCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  formatEarningsLongDate,
  mapMilestoneCommissionItem,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./MilestoneIncentives.css";

const statusClass = (status: string) =>
  `milestone-status milestone-status-${normalizeEarningsStatus(status)}`;

const ShoppingAmount = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { milestoneCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getMilestoneCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = (milestoneCommission.data || []).map(
    mapMilestoneCommissionItem,
  );
  const totalRecords =
    milestoneCommission.pagination?.total ?? records.length;

  const openDetails = (recordId?: string) =>
    navigate(recordId ? `/ShoppingAmountDetail/${recordId}` : "/ShoppingAmount");

  return (
    <section className="milestone-page">
      <div className="milestone-shell">
        <header className="milestone-page-header">
          <div className="milestone-title-wrap">
            <span className="milestone-title-icon">
              <Target size={23} />
            </span>
            <div>
              <span className="milestone-eyebrow">MY REWARDS</span>
              <h1>Milestone Incentives</h1>
              <p>
                Track qualifying purchases, achieved milestones and the
                shopping amount counted toward each reward.
              </p>
            </div>
          </div>

          <div className="milestone-summary">
            <span>Milestone Commission</span>
            <strong>
              {formatEarningsCurrency(
                milestoneCommission.totalMilestoneCommission,
              )}
            </strong>
          </div>
        </header>

        <div className="milestone-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="milestone-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <span className="milestone-record-count">
            {totalRecords} records
          </span>

          <div className="milestone-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search milestone incentives..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="milestone-desktop-list">
          {records.map((record) => (
            <article className="milestone-desktop-card" key={record.id}>
              <div className="milestone-meta-row">
                <div>
                  <span>Date</span>
                  <strong>{formatEarningsDate(record.date)}</strong>
                </div>
                <div>
                  <span>Order ID</span>
                  <strong>{record.orderId || "—"}</strong>
                </div>
                <div>
                  <span>Product Type</span>
                  <strong className="milestone-product-type">
                    {record.orderType}
                  </strong>
                </div>
                <div className="milestone-meta-status">
                  <span>Status</span>
                  <span className={statusClass(record.status)}>
                    {statusLabel(record.status)}
                  </span>
                </div>
              </div>

              <div className="milestone-record-body">
                <div className="milestone-purchased-row">
                  <div>
                    <strong>
                      Purchased On {formatEarningsLongDate(record.date)}
                    </strong>
                    <span>Salar Shopping Voucher</span>
                  </div>
                  <button
                    type="button"
                    className="milestone-view-details"
                    onClick={() => openDetails(record.recordId)}
                  >
                    <Eye size={16} />
                    View Details
                  </button>
                </div>

                <div className="milestone-product-row">
                  <img
                    src={record.productImage || undefined}
                    alt=""
                    className="milestone-product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="milestone-product-copy">
                    <span className="milestone-section-label">
                      QUALIFYING PURCHASE
                    </span>
                    <h2>{record.productName}</h2>
                    <p>
                      {record.milestoneName} · {record.commissionType}
                    </p>
                    <div className="milestone-purchase-tags">
                      <span>
                        <ShoppingBag size={14} />
                        {record.orderType}
                      </span>
                      <span>
                        <CalendarDays size={14} />
                        Credited {formatEarningsDate(record.achievedDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="milestone-info-grid">
                  <div>
                    <span>Voucher Amount</span>
                    <strong>{formatEarningsCurrency(record.grandTotal)}</strong>
                  </div>
                  <div>
                    <span>Milestone Name</span>
                    <strong>{record.milestoneName}</strong>
                  </div>
                  <div className="milestone-amount-box">
                    <span>Gross Commission</span>
                    <strong>
                      {formatEarningsCurrency(
                        record.grossAmount || record.receivedMilestoneCommission,
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>TDS</span>
                    <strong>
                      {formatEarningsCurrency(record.tdsAmount)}
                      {record.tdsIsEstimated ? " (est)" : ""}
                    </strong>
                  </div>
                  <div>
                    <span>Net Credited</span>
                    <strong>
                      {formatEarningsCurrency(
                        record.netAmount ?? record.receivedMilestoneCommission,
                      )}
                    </strong>
                  </div>
                  {record.bonusAmount > 0 && (
                    <div>
                      <span>Bonus</span>
                      <strong>
                        {formatEarningsCurrency(record.bonusNetAmount)}
                        {record.completedWithinTarget && " ✓ Target"}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="milestone-mobile-list">
          {records.map((record) => (
            <button
              type="button"
              className="milestone-mobile-card"
              key={record.id}
              onClick={() => openDetails(record.recordId)}
            >
              <span className="milestone-mobile-index">{record.serialNo}</span>
              <span className="milestone-mobile-copy">
                <span className="milestone-mobile-topline">
                  <span className={statusClass(record.status)}>
                    {statusLabel(record.status)}
                  </span>
                  <span className="milestone-mobile-date">
                    <CalendarDays size={14} />
                    {formatEarningsDate(record.date)}
                  </span>
                </span>
                <strong className="milestone-mobile-title">
                  {record.productName}
                </strong>
                <span className="milestone-mobile-description">
                  {record.milestoneName} · {record.orderId}
                </span>
                <span className="milestone-mobile-bottomline">
                  <strong>
                    {formatEarningsCurrency(
                      record.netAmount ?? record.receivedMilestoneCommission,
                    )}
                  </strong>
                  <span>
                    {record.orderType}
                    {record.tdsAmount != null &&
                      ` · TDS ${formatEarningsCurrency(record.tdsAmount)}`}
                  </span>
                </span>
              </span>
              <ChevronRight
                size={18}
                className="milestone-mobile-chevron"
              />
            </button>
          ))}
        </div>

        {records.length === 0 && (
          <div className="milestone-empty">
            <Target size={30} />
            <strong>No milestone incentives found</strong>
            <span>Change the search or selected date range.</span>
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={milestoneCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default ShoppingAmount;
